/**
 * `AW48` · **Sección 2 · Guardrails**. `R1-F2 §4 Sección 2` y `§5.5`.
 *
 * *«Una lista de categorías. Cada categoría agrupa varios temas bajo un mismo
 * tratamiento.»* Acciones sobre la lista: agregar, editar, eliminar y
 * **reordenar**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ ① EL TIPO DECIDE QUÉ CAMPOS HAY — Y ESO SE VE EN EL FORMULARIO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * | campo | Contención | Moderación |
 * |---|---|---|
 * | Línea de ayuda | **obligatoria** | **no existe** |
 * | Mensaje | opcional | **obligatorio y sin default** |
 * | Marcar para revisión | **no existe** | **obligatorio** |
 *
 * ⛔ **«Marcar para revisión» NO se dibuja en Contención**, y en su lugar va la
 * leyenda. `§4 S2` lo pide textual, y el motivo es duro: `R1-F4 §5.15` hace que
 * una contención marque **siempre, cualquiera sea el valor del campo**, así que
 * dejarlo visible sería *«exhibir un control que no hace nada»* — el defecto que
 * `R1-F4 §5.3` llama «hacer creer que hay un control donde no lo hay».
 *
 * ⛔ **Y la línea de ayuda NO se pide en Moderación**: *«no se le manda una línea
 * de ayuda a alguien que escribió una mala palabra»*.
 *
 * ⚠️ Lo que este archivo NO decide, y es de `R1-F4 §5.15`: **qué hace el motor**
 * con cada tipo, la precedencia entre ellos y el desempate por orden. `§4 S2` lo
 * dice él mismo: *«esa tabla está acá para explicar qué significa el campo Tipo,
 * NO para definir el comportamiento»*.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ ② EL ORDEN ES DATO, Y SE GUARDA CON **UNA** OPERACIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `§5.5`: *«La pantalla guarda el orden de las categorías y lo persiste, y por eso
 * la lista es reordenable.»* Mover una categoría manda **la lista entera de ids**
 * en un solo pedido — no un `PUT` por categoría—: con N ediciones, mover una dos
 * lugares escribiría tres o cuatro filas de auditoría para UNA acción del
 * operador, y la bitácora pasaría a decir que hubo cuatro cambios donde hubo uno.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ ③ EL BORRADO DEVUELVE LA CATEGORÍA COMPLETA, Y SE MUESTRA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Plan_Releases.md:404`: *«sacar una categoría de guardrail sin querer no rompe
 * nada visible, simplemente el chatbot deja de contener»*. El borde devuelve la
 * categoría entera que se borró y acá se dibuja: es lo único que le dice al
 * operador **qué** se llevó puesto, en el caso que por definición no tiene
 * síntoma.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ ④ `AW58` · EL BORRADO SE CONFIRMA ANTES — Y ESTO **ENMIENDA** EL PRD
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⛔ **Esto no repara un incumplimiento: lo contradice a propósito.**
 * `PRD_R1.md:776` dice textual *«Cuando el operador **la elimina y vuelve a abrir
 * la pantalla**»*, y `:833` (`R1-F2-16`) no menciona ninguna confirmación: el
 * borrado de un clic **cumplía** el criterio escrito. La única autorización para
 * cambiarlo es **`ADR-0301`** (`accepted`, 15/9/2026), y ninguna otra. Quien lea
 * sólo el PRD va a creer que esta sección rompe algo.
 *
 * ⛔ **Y el argumento «es irreversible» es FALSO acá**, al revés que en el banco:
 * el evento de auditoría de `AW49` guarda **la categoría entera** como valor
 * anterior, así que un guardrail borrado es RECONSTRUIBLE
 * (`Plan_Releases.md:404` ya había elegido ESA mitigación). Las razones por las
 * que se confirma igual están en `ADR-0301`; no se reescriben acá.
 *
 * ⚠️ **La confirmación NO afloja la auditoría.** Son mitigaciones de tiempos
 * distintos —una actúa antes del clic, la otra después— y el caso que la
 * auditoría cubre, el borrado confirmado a propósito y arrepentido más tarde,
 * queda vivo **justamente porque ahora hay que confirmar**.
 *
 * ⭐ **Se reusa `banco-de-contenido/dialogo.tsx`**, no se estrena un segundo
 * modal: foco atrapado, Escape, y el foco de vuelta al botón que abrió. Un
 * `window.confirm()` o un `<dialog>` propio serían una segunda accesibilidad de
 * modal para mantener. Es el mismo permiso que ya ejerce
 * `simulador/cierre-de-sesion.tsx`.
 *
 * ⛔ **El botón de confirmar NO se rotula «Eliminar»**, sino «Sí, eliminar», que
 * es el rótulo que `banco-de-contenido/acciones.tsx` ya tiene. No es cosmética:
 * `pruebas/entorno.ts` busca botones por **igualdad exacta** de texto y devuelve
 * el primero del contenedor, así que dos botones «Eliminar» —el de la fila y el
 * del diálogo— harían que cuál vuelve dependa del ORDEN EN EL DOM, y el examen
 * pasaría a medir la maquetación en vez del flujo.
 *
 * ⛔ **Y sigue sin haber optimismo.** La fila se queda en la lista mientras el
 * borde trabaja; lo que se dibuja después es **lo que el borde contestó**, no lo
 * que se pidió. Acá importa más que en ningún otro lado: un borrado optimista
 * dejaría la lista diciendo que la categoría no está cuando el borde falló, sobre
 * la superficie que por definición **no tiene ningún síntoma**.
 *
 * ⚠️ **El error del borrado sigue saliendo por `alFallar`, arriba de la
 * pantalla** —no adentro del diálogo, al revés que el banco—, y eso trae una
 * consecuencia que NO es opcional: **el diálogo se cierra cuando el borde
 * contestó, haya fallado o no**. `Dialogo` monta un `.dialogo__fondo` que tapa lo
 * de atrás, así que un modal abierto sobre un error dibujado detrás del overlay
 * sería un error que nadie puede leer. Por eso el cierre cuelga de que
 * `conElBorde` haya TERMINADO, y no de una línea al final del cuerpo: el cuerpo
 * no llega a su última línea cuando el `await` tira.
 */

import { useEffect, useState, type JSX } from "react"
import type {
  CategoriaDeGuardrail,
  CategoriaId,
  NuevaCategoriaDeGuardrail,
  TipoDeGuardrail,
} from "@contracts/admin-api/index.ts"
import {
  DESCRIPCION_DEL_TIPO,
  ROTULO_DEL_TIPO,
  TIPOS_DE_CATEGORIA,
  TOPE_AL_PARTICIPANTE,
  type BordeConfiguracion,
} from "./borde-configuracion.ts"
import { Dialogo } from "../banco-de-contenido/dialogo.tsx"
import { aLosBrazos, losBrazos } from "../zonas.ts"
import { Contador } from "./contador.tsx"
import { URL_NUEVA_CATEGORIA, urlDeLaPestana } from "./rutas.ts"
import { largo, problemasDeLaCategoria, type ProblemaDeCampo } from "./reglas.ts"

interface Props {
  readonly categorias: readonly CategoriaDeGuardrail[]
  readonly borde: BordeConfiguracion
  readonly alCambiar: (categorias: readonly CategoriaDeGuardrail[]) => void
  readonly alFallar: (error: unknown) => void
  /**
   * ⭐ `R1.1-F3-CA-45` (`:553`) · **si la DIRECCIÓN pide el panel de alta.** Lo
   * decide `panelDeCategoriaDelHash()` y llega por prop: esta sección no lee la
   * barra, igual que `envios/pantalla.tsx` no lee la suya.
   */
  readonly abrirNueva?: boolean
  /**
   * ⭐ `R1.1-F5-CA-74` (`:893`) · avisa arriba cuándo el panel está abierto, para
   * que la pantalla repliegue SU acción principal.
   *
   * ⛔ El panel se abre por DOS caminos —la dirección (alta) y el botón «Editar»
   * de una fila (edición, que el PRD no direcciona)— así que la pantalla no lo
   * puede deducir sola de la barra. Sin este aviso, editar dejaría dos acciones
   * principales encendidas y `:796` dice «como máximo una habilitada por
   * pantalla».
   */
  readonly alPanel?: ((abierto: boolean) => void) | undefined
}

/** El formulario arranca vacío y en Contención, que es el tipo que `§5.4` exige que exista. */
const TIPO_INICIAL: TipoDeGuardrail = "contencion"

/**
 * №9 · Dónde se corta el mensaje en la lista.
 *
 * ⛔⛔ **ESTE NÚMERO ES PARTE DE LA REPARACIÓN.** `aw48` afirma
 * `toContain(CONTENCION.mensaje)` sobre el texto de la pantalla entera, y sus
 * fixtures miden **28 caracteres**. Medido el 21/9/2026: **con 20 ese examen se
 * pone rojo; con 90 no.** El valor se eligió con ese margen a la vista, no por
 * estética. Bajarlo por debajo de ~30 rompe `aw48`, y el rojo va a parecer del
 * truncado cuando en realidad es de esta constante.
 *
 * ⚠️ Corta por puntos de código (`Array.from`) y no por `String.length`, por lo
 * mismo que `largo()` en `reglas.ts`: un emoji cuenta como dos unidades UTF-16 y
 * cortar por el medio produce un carácter roto.
 */
const TOPE_EN_LA_LISTA = 90

/** Recorta a `TOPE_EN_LA_LISTA` puntos de código y cierra con «…». */
function recortar(texto: string): string {
  const puntos = Array.from(texto)
  return puntos.length <= TOPE_EN_LA_LISTA
    ? texto
    : `${puntos.slice(0, TOPE_EN_LA_LISTA).join("")}…`
}

interface Formulario {
  readonly editando: CategoriaId | null
  readonly nombre: string
  readonly tipo: TipoDeGuardrail
  readonly temas: string
  readonly mensaje: string
  readonly lineaDeAyuda: string
  readonly marcarParaRevision: boolean
}

const FORMULARIO_VACIO: Formulario = {
  editando: null,
  nombre: "",
  tipo: TIPO_INICIAL,
  temas: "",
  mensaje: "",
  lineaDeAyuda: "",
  marcarParaRevision: true,
}

/** Los temas se escriben separados por coma y se guardan como lista. */
function temasDe(texto: string): readonly string[] {
  return texto
    .split(",")
    .map((tema) => tema.trim())
    .filter((tema) => tema !== "")
}

function formularioDesde(categoria: CategoriaDeGuardrail): Formulario {
  const comun = {
    editando: categoria.id,
    nombre: categoria.nombre,
    temas: categoria.temas.join(", "),
  }
  if (categoria.tipo === "contencion") {
    return {
      ...comun,
      tipo: categoria.tipo,
      mensaje: categoria.mensaje ?? "",
      lineaDeAyuda: categoria.lineaDeAyuda,
      marcarParaRevision: true,
    }
  }
  return {
    ...comun,
    tipo: categoria.tipo,
    mensaje: categoria.mensaje,
    lineaDeAyuda: "",
    marcarParaRevision: categoria.marcarParaRevision,
  }
}

/**
 * Lo que se manda al borde, ya con la forma del contrato.
 *
 * ⭐ **Un `mensaje` vacío en Contención se manda como `null`**, que significa «usá
 * el mensaje de contención genérico de la instancia». En Moderación no hay tal
 * cosa: ahí el vacío es un problema, y lo señala `problemasDeLaCategoria`.
 */
function categoriaDesde(f: Formulario): NuevaCategoriaDeGuardrail {
  const comun = { nombre: f.nombre, temas: temasDe(f.temas) }
  if (f.tipo === "contencion") {
    return {
      ...comun,
      tipo: f.tipo,
      mensaje: f.mensaje.trim() === "" ? null : f.mensaje,
      lineaDeAyuda: f.lineaDeAyuda,
    }
  }
  return { ...comun, tipo: f.tipo, mensaje: f.mensaje, marcarParaRevision: f.marcarParaRevision }
}

export function SeccionDeGuardrails({
  categorias,
  borde,
  alCambiar,
  alFallar,
  /*
   * ⛔⛔ **LAS DOS SON OPCIONALES, Y ESO LO DECIDIÓ UN JUEZ EN ROJO.** Salieron
   * obligatorias y `gates/confirmacion-al-borrar-una-categoria.spec.ts` pasó de 11
   * verdes a 9 rojos: monta esta sección DIRECTAMENTE, con los cuatro props que
   * existían, así que `alPanel` llegaba `undefined` y el efecto reventaba en el
   * montaje — nueve casos rojos por un `TypeError`, ninguno por lo que medían.
   *
   * ⭐ El defecto era mío y la lección es la del contrato: **un campo que se
   * agrega se agrega ADITIVO**. Es lo mismo que `EnrollOutcome.cruce`, que entró
   * opcional para que los implementores y los dobles que no lo declaran siguieran
   * compilando y comportándose igual. Un prop nuevo obligatorio le rompe el
   * montaje a todo consumidor que no lo conozca, y los que no lo conocen son
   * justamente los exámenes que ya estaban en verde.
   *
   * ⚠️ Los defaults NO son «lo que sea»: `abrirNueva = false` es el estado que
   * `CA-45` instaura —el panel cerrado hasta que la dirección lo pida— y el
   * `alPanel` vacío es «nadie está escuchando», que es cierto cuando esta sección
   * se monta suelta.
   */
  abrirNueva = false,
  alPanel,
}: Props): JSX.Element {
  const [formulario, setFormulario] = useState<Formulario>(FORMULARIO_VACIO)
  const [enCurso, setEnCurso] = useState(false)
  const [borrada, setBorrada] = useState<CategoriaDeGuardrail | null>(null)
  /**
   * `AW58` · La categoría cuyo borrado se está PREGUNTANDO. `null` = no se
   * preguntó nada.
   *
   * ⭐ Guarda la categoría entera y no el id porque el diálogo tiene que
   * **nombrarla** —nombre y tipo—: una confirmación genérica («¿estás seguro?»)
   * no le da al operador con qué decidir, y el precedente del banco nombra el
   * sujeto por la misma razón.
   */
  const [aConfirmar, setAConfirmar] = useState<CategoriaDeGuardrail | null>(null)

  /**
   * ⭐ **El panel está abierto por uno de dos caminos**, y son distintos a
   * propósito: el alta la pide la DIRECCIÓN (`CA-45`) y la edición la pide una
   * FILA. El PRD le da dirección a la primera y no a la segunda, así que la
   * segunda sigue siendo estado.
   */
  const panelAbierto = abrirNueva || formulario.editando !== null

  /*
   * `R1.1-F5-CA-74` · se le avisa a la pantalla, que repliega su «Guardar».
   *
   * ⛔ Un efecto y no una llamada en cada manejador: los dos caminos de arriba
   * abren y cierran el panel desde CINCO lugares distintos —el enlace de alta, el
   * botón «Editar», «Cancelar», el guardado que limpia el formulario y el borrado
   * de la categoría que se estaba editando—. Avisar en cada uno es cinco sitios
   * que pueden divergir; avisar sobre el derivado es uno solo, y no puede
   * desincronizarse de lo que la pantalla realmente dibuja.
   */
  useEffect(() => {
    alPanel?.(panelAbierto)
  }, [panelAbierto, alPanel])

  const propuesta = categoriaDesde(formulario)
  const problemas: readonly ProblemaDeCampo[] = problemasDeLaCategoria(propuesta)
  const problemaDe = (campo: string): ProblemaDeCampo | undefined =>
    problemas.find((p) => p.campo === campo)

  const conElBorde = async (accion: () => Promise<void>): Promise<void> => {
    setEnCurso(true)
    try {
      await accion()
    } catch (error) {
      alFallar(error)
    } finally {
      setEnCurso(false)
    }
  }

  const guardar = (): void => {
    if (problemas.length > 0) return
    void conElBorde(async () => {
      if (formulario.editando === null) {
        const creada = await borde.crearCategoria(propuesta)
        alCambiar([...categorias, creada])
      } else {
        const id = formulario.editando
        const editada = await borde.editarCategoria(id, propuesta)
        alCambiar(categorias.map((c) => (c.id === id ? editada : c)))
      }
      setFormulario(FORMULARIO_VACIO)
      setBorrada(null)
    })
  }

  /**
   * ⛔ `AW58` · Esto sólo corre DESPUÉS de que el operador confirmó. El clic en
   * «Eliminar» de la fila no llega hasta acá: abre el diálogo y no le pide nada
   * al borde.
   *
   * ⛔⛔ **El cierre del diálogo NO va adentro del cuerpo.** Si estuviera después
   * del `await`, un borde que tira dejaría el modal abierto sobre un error que se
   * dibuja detrás del overlay —el `alFallar` sale arriba de la pantalla—, y ese
   * es exactamente el caso que hay que cubrir. `conElBorde` captura el error y su
   * promesa resuelve igual, así que colgar el cierre de que TERMINÓ es lo que
   * hace que el diálogo se cierre haya fallado o no.
   *
   * ⛔ **Y no hay optimismo**: la lista se recalcula con `quitada`, que es **lo
   * que el borde contestó**, no con la categoría que se pidió borrar.
   */
  const eliminar = (categoria: CategoriaDeGuardrail): void => {
    void conElBorde(async () => {
      const quitada = await borde.borrarCategoria(categoria.id)
      alCambiar(categorias.filter((c) => c.id !== quitada.id))
      setBorrada(quitada)
      if (formulario.editando === quitada.id) setFormulario(FORMULARIO_VACIO)
    }).finally(() => {
      setAConfirmar(null)
    })
  }

  /** Mueve una categoría un lugar y persiste **la lista entera**, en un pedido. */
  const mover = (desde: number, hacia: number): void => {
    if (hacia < 0 || hacia >= categorias.length) return
    const reordenadas = [...categorias]
    const [movida] = reordenadas.splice(desde, 1)
    if (movida === undefined) return
    reordenadas.splice(hacia, 0, movida)
    void conElBorde(async () => {
      const guardadas = await borde.reordenarCategorias(reordenadas.map((c) => c.id))
      alCambiar(guardadas)
    })
  }

  const hayContencion = categorias.some((c) => c.tipo === "contencion")

  return (
    <section
      className="configuracion-chatbot__seccion"
      aria-labelledby="configuracion-chatbot-guardrails"
    >
      <h2 id="configuracion-chatbot-guardrails">Guardrails</h2>

      {/*
        ⭐ `R1.1-F3-CA-43` y `CA-44` (`PRD_R1.1.md:551-552`) · **qué hace cada
        tipo**, con las dos frases textuales del PRD.

        ⛔ No es redundante con el `<select>` de tipo: ahí los dos rótulos son
        «Contención» y «Moderación», y **ninguno dice qué pasa al elegir uno u
        otro**. Ésta es la única pantalla donde el operador puede enterarse de la
        diferencia, y la diferencia incluye si la conversación sigue o se marca
        para que alguien la mire.

        ⛔⛔ **VAN COMO `<p>` Y NO COMO `<ul><li>`, Y ESO LO DECIDIÓ UN JUEZ EN
        ROJO.** La primera versión las puso en una lista y
        `gates/confirmacion-al-borrar-una-categoria.spec.ts` pasó de 11 verdes a 7
        rojos: cuenta las FILAS de categorías como «todo `<li>` que no esté dentro
        de un `[role="alert"]"` (`:411`), así que estos dos renglones entraban al
        conteo y la escena de dos categorías declaraba cuatro.

        ⚠️ El juez tiene razón en lo que mide y el defecto era mío: **esto no es
        una lista de categorías**, y dibujarlo como lista lo decía en el único
        idioma que ese selector entiende. Dos `<p>` dicen lo mismo al operador y
        no se hacen pasar por filas.
      */}

      {/*
        `§5.4` · «al menos una categoría tiene que ser de tipo Contención». Se
        AVISA y no se bloquea: quien lo hace cumplir es el arranque del proceso, y
        bloquear el borrado de la última contención dejaría al operador sin poder
        corregir una categoría mal cargada.
      */}
      {hayContencion ? null : (
        <p className="configuracion-chatbot__alerta" role="alert">
          No hay ninguna categoría de Contención. El proceso no arranca sin al menos una: un
          chatbot que le habla a menores y no reacciona ante una crisis.
        </p>
      )}

      {/*
        ⭐ `R1.1-F3-CA-45` (`PRD_R1.1.md:553`) · *«tocar “+ Nueva categoría” lleva a
        `#/instancia/configuracion-del-chatbot/guardrails/nueva`»*.

        ⛔⛔ **Es un `<a>` y no un `<button>`, y no es una preferencia de estilo.**
        El criterio dice «LLEVA A» una dirección: eso es navegar, y navegar tiene
        un elemento. Con un `<button>` el operador no puede abrirlo en otra
        pestaña, ni copiar el enlace, ni volver con el botón de atrás — y el PRD
        le da dirección propia justamente para que se pueda compartir. Es el mismo
        camino que ya usan las filas de Mensajes del motor (`pantalla.tsx:538`).

        ⚠️ **Y NO lleva `boton--primario`.** Con el panel cerrado la principal de
        la pantalla es «Guardar»; con el panel abierto es «Guardar categoría»
        (`R1.1-F5-CA-74`). En ninguno de los dos estados la principal es ésta, y
        `:796` deja «como máximo una acción principal habilitada por pantalla».
      */}
      {/*
        ⭐ №3 · **LA MITAD DE POSICIÓN, Y SÓLO ESA.** El boceto lo dibuja arriba a
        la derecha, en la fila de la intro — eso es `②` y se hace. Pero el boceto
        también lo pinta PRIMARIO (`btn btn-p`), y **eso es `①` y no se hace**:

        > `R1.1-F5-CA-73` (`PRD_R1.1.md:1157`) · *«Cada pantalla nueva de brazo o
        > transversal tiene como máximo una acción principal: **sin popups ni
        > paneles laterales abiertos**, ninguna de esas pantallas tiene más de un
        > botón habilitado con fondo de color (incluido `#1d1d1f`).»*

        En `…/guardrails` sin panel abierto la principal ya es «Guardar» (la barra
        global), así que un segundo botón con fondo de color pone `CA-73` en rojo.
        `PRD_R1.1.md:1061` lo repite y aclara que los paneles laterales **no
        cuentan para la pantalla**, así que el panel abierto no lo salva.

        ⇒ **Se mueve, no se pinta.** Sigue sin `boton--primario`.
      */}
      {/*
        ⛔⭐ `[21/9/2026]` **EL ENVOLTORIO TENÍA UN SOLO HIJO, Y POR ESO EL BOTÓN
        SEGUÍA A LA IZQUIERDA.** `.configuracion-chatbot__encabezado-lista` es
        `display:flex; justify-content:space-between`, y con **un** hijo no hay
        nada que separar: el botón se quedaba pegado al borde izquierdo, debajo de
        la intro. El operador lo vio en una captura.

        ⇒ La intro entra ACÁ, como primera mitad de la fila. Es lo que dibuja el
        boceto: el párrafo a la izquierda, el botón a la derecha, alineados por la
        primera línea de texto (`align-items: baseline`).

        ⚠️ El `<p>` no cambió de texto ni de clase — sólo de padre. Lo que
        cambia es quién lo contiene, no qué dice.
      */}
      <div className="configuracion-chatbot__encabezado-lista">
        <p className="configuracion-chatbot__ayuda">
          Cada categoría agrupa varios temas bajo un mismo tratamiento. El orden de la lista se
          guarda: el motor lo usa para desempatar entre categorías del mismo tipo.
        </p>
        {/*
          ⭐⭐ `[21/9/2026]` **ES LA ACCIÓN PRINCIPAL DE LA LISTA — decisión del
          dueño, tomada con la medición delante.**

          El boceto lo dibuja `<span class="btn btn-p">`, o sea primario, y hasta
          hoy acá decía `"boton"` a secas porque el portador del color en esta
          pantalla era el «Guardar» de la barra. `R1.1-F5-CA-73` deja **uno solo**
          con fondo, así que no se podía tener los dos.

          ⇒ **El portador se MUEVE, no se duplica.** `pantalla.tsx` despinta su
          «Guardar» cuando la pestaña activa es Guardrails, y el color viene acá.
          La lista sigue teniendo exactamente uno.

          ⛔ **Y se repliega con el panel, igual que el otro.** Sin el
          `!panelAbierto`, abrir «Nueva categoría» o «Editar» dejaría este botón
          pintado JUNTO al «Guardar categoría» del panel: dos principales, que es
          exactamente lo que `R1.1-F5-CA-74` prohíbe. El defecto se habría visto
          sólo al abrir el panel.
        */}
        <a
          className={panelAbierto ? "boton" : "boton boton--primario"}
          href={URL_NUEVA_CATEGORIA}
        >
          + Nueva categoría
        </a>
      </div>

      {categorias.length === 0 ? (
        <p className="configuracion-chatbot__vacio">Todavía no hay categorías cargadas.</p>
      ) : (
        /*
          ⛔⛔ `R1.1-F3-CA-42` (`PRD_R1.1.md:550`) · `[17/9/2026]` · `ADR-0082`.
          **ESTO ERA UN `<ul>` DE `<li>`** y el criterio pide una TABLA con tres
          columnas: *«la tabla tiene las columnas Orden, Categoría y temas y
          Mensaje que se envía»*. `CP-R1-1-F3-042` lo midió: los tres encabezados
          faltaban porque no había ningún `<th>` en el archivo.

          ⭐ **La información ya estaba toda; lo que faltaba era la forma.** Cada
          `<li>` tenía nombre, tipo, temas, mensaje y acciones. Acá se reparten en
          las tres columnas que el criterio nombra, sin perder ni un dato:

            · `Orden`                → la posición, 1-based, + Subir/Bajar
            · `Categoría y temas`    → nombre, etiqueta de tipo y temas
            · `Mensaje que se envía` → el mensaje, la línea de ayuda o el aviso
                                       de revisión, y Editar/Eliminar

          ⚠️⚠️ **Y ESTO DEROGA LA NOTA DE ARRIBA SOBRE LOS `<p>`**, que decía que
          las dos explicaciones no van como `<ul><li>` porque
          `gates/confirmacion-al-borrar-una-categoria.spec.ts` cuenta las filas
          como «todo `<li>` que no esté en un `[role=alert]`». **Esa razón venció
          por el otro lado**: ya no hay ningún `<li>` de categoría que contar, así
          que el juez no puede seguir contando `<li>`. Su selector se enmienda
          —cómo cuenta, no qué afirma— y queda anotado allá.

          ⭐ Los dos `<p>` de explicación **se quedan como `<p>`** igual: ahora no
          es por el juez, es porque siguen sin ser filas de una tabla de
          categorías.
        */
        <table className="configuracion-chatbot__categorias">
          <thead>
            <tr>
              {/*
                ⛔ Los tres rótulos son los del criterio, textuales. «Categoría y
                temas» y «Mensaje que se envía» se leen raro como encabezado de
                columna y así los pide el PRD: son los que el operador va a buscar.
              */}
              <th scope="col">Orden</th>
              <th scope="col">Categoría y temas</th>
              <th scope="col">Mensaje que se envía</th>
            </tr>
          </thead>
          <tbody>
            {categorias.map((categoria, i) => (
              <tr key={categoria.id} className="configuracion-chatbot__categoria">
                <td className="configuracion-chatbot__orden">
                  {/*
                    ⭐ 1-based y no el índice: es lo que el operador ve como
                    «orden», y `Subir`/`Bajar` siguen operando sobre `i`.
                  */}
                  <span className="configuracion-chatbot__posicion">{i + 1}</span>
                  <div className="configuracion-chatbot__acciones">
                    <button
                      type="button"
                      className="boton boton--chico"
                      onClick={() => {
                        mover(i, i - 1)
                      }}
                      disabled={enCurso}
                      aria-label={`Subir ${categoria.nombre}`}
                    >
                      Subir
                    </button>
                    <button
                      type="button"
                      className="boton boton--chico"
                      onClick={() => {
                        mover(i, i + 1)
                      }}
                      disabled={enCurso}
                      aria-label={`Bajar ${categoria.nombre}`}
                    >
                      Bajar
                    </button>
                  </div>
                </td>

                <td>
                  <div className="configuracion-chatbot__categoria-cabecera">
                    <strong>{categoria.nombre}</strong>
                    {/*
                      ⭐ №6 · el tipo es un CHIP con borde de color, no texto
                      pelado (boceto: `<span class="estado tipo-cont">`).

                      ⛔ **Contención usa `--atencion`, que YA ERA `#8a5a12`** —el
                      color que el boceto pide— y `§1.3` lo nombra «Atención,
                      pendiente, pausado». **Moderación va NEUTRA**: el `#0063c7`
                      del boceto es el color del Brazo Puro, y
                      `sistema-visual.md:39` (prioridad 2) dice *«El color de
                      brazo se usa solo en: sello, miga, botón principal, pestaña
                      activa, selección y foco. **Nunca decora.»*, con `ADR-0316`
                      («un significado por portador»). §1.3 no tiene color para
                      Moderación. No pierde nada: §1.3 fija que *«el estado nunca
                      se comunica solo con color: siempre va con texto»*, y el
                      chip dice MODERACIÓN con todas las letras.
                    */}
                    <span
                      className={`configuracion-chatbot__tipo configuracion-chatbot__tipo--${
                        categoria.tipo === "contencion" ? "contencion" : "moderacion"
                      }`}
                    >
                      {ROTULO_DEL_TIPO[categoria.tipo]}
                    </span>
                  </div>
                  {/*
                    ⭐ №7 · los temas son PÍLDORAS, una por tema (boceto:
                    `<span class="chip">`), no una cadena unida por comas.

                    ⛔ **Esto obligó a enmendar dos `expect` de `aw48`**, y la
                    autorización es que `toContain(temas.join(", "))` afirmaba la
                    FORMA y ninguna fuente la pide: `R1.1-F3-CA-48` exige
                    «Separados por coma…» **como ayuda del INPUT del panel**, no
                    como presentación en la lista, y `CA-42` sólo nombra la
                    columna «Categoría y temas». Lo que los casos afirman sigue
                    siendo el DATO: que los temas estén visibles.
                  */}
                  <p className="configuracion-chatbot__temas">
                    {categoria.temas.map((tema) => (
                      <span key={tema} className="configuracion-chatbot__chip">
                        {tema}
                      </span>
                    ))}
                  </p>
                </td>

                <td>
                  {/*
                    ⭐ №9 · el mensaje va TRUNCADO en la lista (boceto: termina en
                    «…»). El texto completo se lee en el panel de edición.

                    ⛔⛔ **EL UMBRAL ES PARTE DE LA REPARACIÓN, NO UN DETALLE DE
                    GUSTO.** `aw48` afirma `toContain(CONTENCION.mensaje)` sobre
                    el texto de la pantalla, y sus fixtures son de **28
                    caracteres**: **un umbral de 20 pone `aw48` en rojo y uno de
                    90 no.** Medido el 21/9/2026 antes de elegirlo. Si alguien lo
                    baja, el rojo que aparece NO es del truncado: es de este
                    número.
                  */}
                  <p className="configuracion-chatbot__mensaje">
                    {recortar(
                      categoria.mensaje ??
                        "Sin mensaje propio: usa el de contención de la instancia.",
                    )}
                  </p>
                  {categoria.tipo === "contencion" ? (
                    <p className="configuracion-chatbot__linea">
                      Línea de ayuda: {categoria.lineaDeAyuda}
                    </p>
                  ) : (
                    <p className="configuracion-chatbot__linea">
                      {categoria.marcarParaRevision
                        ? "Marca el caso para revisión."
                        : "No marca el caso para revisión."}
                    </p>
                  )}
                  <div className="configuracion-chatbot__acciones">
                    <button
                      type="button"
                      className="boton boton--chico"
                      onClick={() => {
                        setFormulario(formularioDesde(categoria))
                      }}
                      disabled={enCurso}
                    >
                      Editar
                    </button>
                    {/*
                      ⛔ `AW58` · ESTE CLIC NO LE PIDE NADA AL BORDE. Abre el
                      diálogo y nada más. Si alguna vez vuelve a llamar a
                      `eliminar` directo, el modal pasa a dibujarse encima de un
                      borrado que YA ocurrió.
                    */}
                    <button
                      type="button"
                      className="boton boton--chico"
                      onClick={() => {
                        setAConfirmar(categoria)
                      }}
                      disabled={enCurso}
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/*
        ⭐⭐ ⟦la-leyenda-de-tipos-va-DEBAJO-de-la-lista⟧ · el `D-1` de la
        clasificación de los 98 deltas (`G6`, `G7`, `N8`, `N9`), devuelto con
        objeción el 21/9/2026 y **resuelto ese mismo día a favor del boceto**.

        ⛔ **La objeción era mía y estaba mal dirigida:** cité `PRD_R1.1.md:669`,
        que es la fila del **PANEL**. La fila de la **LISTA** es `:668`, y es un
        INVENTARIO, no un orden — se prueba solo: pone *«leyenda de tipos; "+
        Nueva categoría"»* en ese orden, mientras el boceto dibuja «+ Nueva
        categoría» **arriba a la derecha**. Una enumeración que no acierta la
        posición de un ítem no puede arbitrar la de otro.

        ⇒ Ningún criterio nombra el ORDEN de estos bloques, así que manda el
        boceto (`ConfigGuardrails.dc.html`, que pone `<div class="ayuda">`
        **después** de `<div class="lista">`). Clasificación `R1.1-F5`: **②**.

        ⚠️ `R1.1-F3-CA-43` y `CA-44` sólo piden que las dos frases **se lean** en
        esta pantalla; no dicen dónde. Siguen verdes: el texto no cambió ni una
        letra.

        ⛔⛔ **VAN COMO `<p>` Y NO COMO `<li>`, Y ESO LO DECIDIÓ UN JUEZ EN ROJO.**
        Una versión anterior las puso en una lista y
        `gates/confirmacion-al-borrar-una-categoria.spec.ts` pasó de 11 verdes a
        7 rojos: cuenta las filas de categorías como «todo `<li>` que no esté
        dentro de un `[role="alert"]`», así que estos dos renglones entraban al
        conteo y una escena de dos categorías declaraba cuatro.

        ⛔ **Y por eso van ACÁ, fuera del `<table>` y no dentro del `<tbody>`.**
        Ese mismo juez hoy cuenta `tbody tr`: dos renglones metidos en el cuerpo
        de la tabla lo volverían a poner en rojo por el mismo motivo, con otra
        etiqueta. Mover un bloque «debajo de la lista» **no es meterlo adentro**.
      */}
      <p className="configuracion-chatbot__ayuda">
        CONTENCIÓN responde con mensaje + línea de ayuda y siempre marca el caso para revisión.
      </p>
      <p className="configuracion-chatbot__ayuda">
        MODERACIÓN responde con el mensaje y la conversación sigue.
      </p>

      {/*
        El borrado devuelve la categoría COMPLETA. Se muestra porque este borrado
        es el que no tiene ningún síntoma: el chatbot simplemente deja de contener.
      */}
      {borrada === null ? null : (
        <p className="configuracion-chatbot__nota" role="note">
          Se eliminó «{borrada.nombre}» ({ROTULO_DEL_TIPO[borrada.tipo]}), con sus temas:{" "}
          {borrada.temas.join(", ")}.
        </p>
      )}

      {/* ── `AW58` · LA CONFIRMACIÓN ──────────────────────────────────────────
        ⛔ Va ANTES del formulario a propósito: el «Cancelar» del formulario de
        edición existe cuando se está editando, y `pruebas/entorno.ts` busca
        botones por igualdad exacta de texto devolviendo **el primero** del
        contenedor. Con el diálogo acá, el «Cancelar» que vuelve es el del
        diálogo aunque haya una edición abierta atrás.

        ⛔ `Dialogo` NO usa portal: este nodo queda DENTRO de la sección. Por eso
        el selector de la nota posterior va con `>` y no con descendiente — si
        no, el diálogo sería una tercera cosa que pisarlo.
      */}
      {aConfirmar === null ? null : (
        <Dialogo
          /*
           * ⭐ ENMIENDA `R1.1-F3-CA-61` (`PRD_R1.1.md:569`) · ~~«Eliminar categoría»~~
           * → **«¿Eliminar «<nombre>»?»**. El criterio pide el título con la
           * categoría adentro y en pregunta.
           *
           * ⚠️ Y el rótulo viejo NO desaparece del diálogo: `R1.1-F5-CA-94`
           * (`:913`) lo pide como rótulo del BOTÓN de confirmar. Son dos lugares
           * distintos, y ese cambio espera a que `gates/` se enmiende primero.
           */
          titulo={`¿Eliminar «${aConfirmar.nombre}»?`}
          onCerrar={() => {
            setAConfirmar(null)
          }}
        >
          {/*
            ⭐ Se NOMBRA la categoría y su tipo. Una confirmación genérica no le
            da al operador con qué decidir, y el precedente del banco nombra el
            documento por la misma razón.

            ⛔ NO se enumeran los temas: el precedente nombra sólo el sujeto, y
            una categoría con muchos temas mete una lista larga en un cuadro de
            decisión. Quien los quiera ver los tiene en la nota posterior.
          */}
          {/*
            ⛔⛔ ENMIENDA `R1.1-F3-CA-64` (`PRD_R1.1.md:572`) · acá decía, textual:

              ~~«Y acá NO va el “no se puede deshacer” del banco, que no es un
              olvido: sería FALSO. El evento de auditoría de `AW49` guarda la
              categoría entera, así que esto es RECONSTRUIBLE (`ADR-0301`).»~~

            El dato técnico sigue siendo cierto —`AW49` guarda la categoría y
            `ADR-0301` la hace reconstruible— pero la conclusión no se sostiene,
            y el PRD pide la frase con todas las letras.

            ⭐ No es falsa porque las dos oraciones hablan de cosas distintas: el
            rastro dice que un DESARROLLADOR puede reconstruirla desde la
            auditoría; el criterio le habla al OPERADOR, que no tiene deshacer,
            ni papelera, ni ninguna pantalla desde donde volver atrás. En su
            marco, «No se puede deshacer» es exactamente lo que pasa — que es por
            lo mismo que el Banco la dice.

            ⚠️ Queda dicho igual, y arriba: si algún día se ofrece restaurar desde
            la auditoría, esta frase pasa a ser falsa y hay que volver acá.
          */}
          <p>
            Se va a eliminar la categoría <strong>{aConfirmar.nombre}</strong> (
            {ROTULO_DEL_TIPO[aConfirmar.tipo]}) de los guardrails del chatbot: va a dejar de
            contener sus temas, y eso no se nota en ninguna otra pantalla.
          </p>

          {/* `CA-62` (`:570`) · el alcance y el momento, textual. */}
          {/* ⟦rastro · 30/9/2026⟧ ~~«para los dos brazos»~~ fijo: sale de `losBrazos()`; sobre `KAI` es idéntico. */}
          <p>Se elimina para {losBrazos()} y el cambio queda vigente al instante.</p>

          {/*
            ⭐ `CA-63` (`:571`) · **acá SÍ se enumeran los temas**, y eso deroga lo
            que este archivo decía dos bloques más arriba (~~«NO se enumeran los
            temas»~~): el criterio los pone en la frase. La razón vieja —que una
            categoría con muchos temas mete una lista larga en un cuadro de
            decisión— sigue siendo verdad y es el riesgo que el PRD aceptó.

            ⛔ El tratamiento sale del TIPO y no de un literal. Escribir
            «moderación» fijo deja verde el caso del criterio —cuya categoría es
            de Moderación— y le miente al operador en cada categoría de
            Contención, que por `§5.4` son las que siempre existen.
          */}
          <p>
            Desde ese momento los temas {aConfirmar.temas.join(", ")} ya no reciben el mensaje de{" "}
            {ROTULO_DEL_TIPO[aConfirmar.tipo].toLocaleLowerCase("es")}.
          </p>

          {/* `CA-64` (`:572`) · textual, y el porqué está en el bloque de arriba. */}
          <p>No se puede deshacer.</p>

          <footer className="dialogo__acciones">
            {/*
              ⛔ El rótulo NO es «Eliminar»: ya hay uno exacto en cada fila, y dos
              botones con el mismo texto vuelven al examen una medición del orden
              del DOM. «Sí, eliminar» es el que el repo ya tiene.
            */}
            <button
              type="button"
              className="boton boton--peligro-solido"
              onClick={() => {
                eliminar(aConfirmar)
              }}
              disabled={enCurso}
            >
              {/*
                ⭐ ENMIENDA `R1.1-F5-CA-94` (`PRD_R1.1.md:913`) · ~~«Sí, eliminar»~~
                → **«Eliminar categoría»**. El rótulo nombra la acción, que es lo
                que el criterio pide.

                ⛔ La objeción que este archivo tenía escrita —*«el rótulo NO es
                “Eliminar”: ya hay uno exacto en cada fila, y dos botones con el
                mismo texto vuelven al examen una medición del orden del DOM»*—
                **sigue en pie y sigue respetada**: es cierta de «Eliminar» y
                falsa de «Eliminar categoría», que es único en la pantalla. La
                restricción que protegía no se aflojó; cambió el texto que la
                cumple.

                ⚠️ El juez se enmendó ANTES que esta línea, a propósito
                (`gates/confirmacion-al-borrar-una-categoria.spec.ts`,
                `ee45370c`): si el sujeto y el examen se mueven a la vez, uno de
                los dos mide un árbol roto.
              */}
              {enCurso ? "Eliminando…" : "Eliminar categoría"}
            </button>
            <button
              type="button"
              className="boton"
              onClick={() => {
                setAConfirmar(null)
              }}
              disabled={enCurso}
            >
              Cancelar
            </button>
          </footer>
        </Dialogo>
      )}

      {!panelAbierto ? null : (
      /*
       * ══════════════════════════════════════════════════════════════════════
       * ⛔⛔ ⟦esto-es-un-PANEL-LATERAL-y-antes-no-lo-era⟧ · `[18/9/2026]`
       * ══════════════════════════════════════════════════════════════════════
       *
       * `PRD_R1.1.md:669` pide, textual, **«Panel lateral»**, y el boceto «Config.
       * chatbot · Nueva / editar categoría» lo dibuja **encima de la lista, con su
       * cabecera y su ✕**.
       *
       * ⛔ **Acá había un `<div class="…__formulario">` en FLUJO NORMAL**, con una
       * hoja de dos líneas (`margin-top` y `max-width`): se dibujaba **debajo de la
       * lista**, sin cabecera, sin separación y sin salida más que el «Cancelar»
       * del pie. Lo reportó una persona mirando la pantalla.
       *
       * ⛔⛔ **Y los exámenes estaban TODOS en verde**, incluidos los de
       * `r11-popup-de-categoria.test.tsx` que se llaman «el panel de alta»: afirman
       * que `#guardrail-nombre` **existe o no existe** según la dirección —que era
       * cierto— y **ninguno afirmaba DÓNDE**. Un barrido de DOM tampoco lo ve:
       * happy-dom no maqueta. Se vio leyendo la hoja de estilos.
       *
       * ⚠️ **No se reusa `Dialogo`**, a diferencia del borrado: aquél es modal
       * —atrapa el foco y bloquea todo lo de atrás— y esto es un panel de EDICIÓN
       * con dirección propia (`CA-45`), que tiene que sobrevivir a recargar y a
       * copiar el enlace. Un modal con URL propia es un modal del que se sale con
       * «atrás», y ésa es otra discusión.
       */
      <aside
        className="configuracion-chatbot__panel-lateral"
        aria-labelledby="configuracion-chatbot-titulo-del-panel"
      >
        <header className="configuracion-chatbot__panel-cabecera">
          {/*
            ⭐ La zona, textual del boceto. Repite el aviso transversal adentro del
            panel porque el panel TAPA el de la pantalla: quien edita una categoría
            acá adentro tiene que seguir viendo que lo que toca rige para los dos
            brazos. Es el mismo criterio de `CA-11`/`CA-12` aplicado al panel.
          */}
          <p className="configuracion-chatbot__panel-zona">Guardrails · aplica {aLosBrazos()}</p>
          <h3 id="configuracion-chatbot-titulo-del-panel">
            {formulario.editando === null ? "Nueva categoría" : "Editar categoría"}
          </h3>
          {/*
            ⭐ La ✕ del boceto. **Hace lo mismo que «Cancelar»** —y por eso sale por
            donde entró, igual que aquél— pero se dibuja arriba a la derecha, que es
            donde se la busca en un panel. Lleva nombre accesible propio: una ✕ sola
            se anuncia como «✕».
          */}
          {formulario.editando !== null ? (
            <button
              type="button"
              className="configuracion-chatbot__panel-cerrar"
              aria-label="Cerrar el panel de categoría"
              onClick={() => {
                setFormulario(FORMULARIO_VACIO)
              }}
              disabled={enCurso}
            >
              ✕
            </button>
          ) : (
            <a
              className="configuracion-chatbot__panel-cerrar"
              aria-label="Cerrar el panel de categoría"
              href={urlDeLaPestana("guardrails")}
            >
              ✕
            </a>
          )}
        </header>

        <div className="configuracion-chatbot__campo">
          <label htmlFor="guardrail-nombre">Nombre</label>
          <input
            id="guardrail-nombre"
            type="text"
            value={formulario.nombre}
            onChange={(e) => {
              setFormulario({ ...formulario, nombre: e.target.value })
            }}
            aria-invalid={problemaDe("nombre") !== undefined}
          />
        </div>

        {/*
          ⭐⭐ ⟦las-DOS-descripciones-del-tipo-se-leen-juntas⟧ · `PRD_R1.1.md:669`
          enumera **las dos**: *«Tipo (Contención: «Mensaje + línea de ayuda.
          Siempre marca el caso para revisión.» / Moderación: «Solo el mensaje.
          La conversación sigue.»)»*, y el boceto `ConfigGuardrailCategoria.dc.html`
          las dibuja como dos tarjetas (`.opcion` / `.opcion.on`).

          ⛔ **Hasta el 21/9/2026 acá había un `<select>`**, y con él sólo se leía
          la descripción de la opción **ya elegida**: el operador **no podía
          comparar antes de decidir**, en la pantalla que clasifica contenido de
          autolesión. Lo encontró una PERSONA mirando la pantalla construida;
          ningún examen del repo lo veía, ni el barrido de `r11-orden-de-bloques`.

          ⭐ Clasificación `R1.1-F5` · **②**: el defecto lo nombra `:669`, que es
          la tabla de descripción funcional. Se buscó un criterio cuyo SUJETO
          fuera la forma del control —que lo volvería ① e intocable— y **no
          existe**: `CA-53`, `CA-54` y `CA-55` dicen «con Tipo Contención», o sea
          el VALOR; los criterios que dicen «desplegable» (`R1.1-F2-CA-52…60`)
          son de elegir brazo en otras pantallas; y ningún gate mira este control.

          ⭐ **Radios NATIVOS en `<fieldset>`/`<legend>`**, no dos `<div>` con
          `role="radiogroup"`: el navegador ya da navegación por flechas,
          agrupamiento y estado anunciado, sin `tabindex` ni manejo de teclas
          escrito a mano (`ADR-0233 ②`).

          ⚠️ **EL ACOPLAMIENTO QUE HAY QUE SABER ANTES DE TOCAR ESTE CONTROL.**
          `pruebas/aw48-configuracion-del-chatbot.test.tsx` lo **acciona** en el
          caso `R1-F2-14 · «Marcar para revisión» NO está en el árbol en
          Contención…` (hoy en `:431`, pero **buscalo por el nombre del caso**:
          la cita por número ya se quedó vieja una vez — decía `:399`). Lo agarra
          **por id y por tipo**, no por texto, así que **ningún grep de texto lo
          encuentra**. Antes apretaba un `<select>` con `seleccionar()`; ahora
          aprieta `#guardrail-tipo-moderacion` con `alternar()`. Si cambiás la
          forma de este control, ese caso se rompe y es el único que avisa.
        */}
        {/*
          ⛔⛔ ⟦el-fieldset-NO-lleva-__campo⟧ · **SIN ESTA CLASE MENOS, LAS DOS
          TARJETAS SE APILAN.** Medido el 21/9/2026:

            · `.configuracion-chatbot__campo`   → `display:flex; flex-direction:column`
            · `.configuracion-chatbot__opciones` → `display:flex` **y NADA de
              `flex-direction`**

          Misma especificidad (una clase), y `__opciones` está DESPUÉS en la
          hoja — pero como no declara `flex-direction`, **el `column` de
          `__campo` sobrevive la cascada** y el `display:flex` de `__opciones`
          parece no hacer nada. El boceto las pone lado a lado
          (`<div style="display:flex;gap:12px">`).

          ⭐ Se saca `__campo` en vez de agregarle `flex-direction:row` a la
          hoja, porque `__opciones` **ya trae lo único que `__campo` aportaba**:
          `margin: var(--e4) 0 0` y `max-width: var(--ancho-documento)`. Cero
          CSS nuevo, y una clase menos que se pise con otra.

          ⛔ **Ningún examen de este repo puede ver esto.** `happy-dom` no
          resuelve la cascada ni maqueta: apilado y lado a lado son el MISMO DOM.
          Lo único afirmable es qué clases lleva el elemento, y es lo que mide
          `pruebas/r11-pie-de-paneles.test.tsx`. Que se vean lado a lado lo mira
          una persona — que es como apareció.
        */}
        <fieldset className="configuracion-chatbot__opciones">
          <legend>Tipo</legend>
          {TIPOS_DE_CATEGORIA.map((tipo) => (
            <label
              key={tipo}
              className={
                formulario.tipo === tipo
                  ? "configuracion-chatbot__opcion configuracion-chatbot__opcion--activa"
                  : "configuracion-chatbot__opcion"
              }
            >
              <input
                type="radio"
                name="guardrail-tipo"
                id={`guardrail-tipo-${tipo}`}
                value={tipo}
                checked={formulario.tipo === tipo}
                onChange={() => {
                  setFormulario({ ...formulario, tipo })
                }}
              />
              <strong>{ROTULO_DEL_TIPO[tipo]}</strong>
              {/* ⟦DESCRIPCION_DEL_TIPO⟧ en `borde-configuracion.ts`. */}
              <span className="configuracion-chatbot__ayuda">{DESCRIPCION_DEL_TIPO[tipo]}</span>
            </label>
          ))}
        </fieldset>

        <div className="configuracion-chatbot__campo">
          <label htmlFor="guardrail-temas">Temas</label>
          <input
            id="guardrail-temas"
            type="text"
            value={formulario.temas}
            onChange={(e) => {
              setFormulario({ ...formulario, temas: e.target.value })
            }}
            aria-invalid={problemaDe("temas") !== undefined}
            placeholder="autolesión, ideación suicida, suicidio"
          />
          <span className="configuracion-chatbot__ayuda">
            Separados por coma. Todos disparan el mismo mensaje.
          </span>
        </div>

        <div className="configuracion-chatbot__campo">
          <label htmlFor="guardrail-mensaje">Mensaje</label>
          <textarea
            id="guardrail-mensaje"
            value={formulario.mensaje}
            onChange={(e) => {
              setFormulario({ ...formulario, mensaje: e.target.value })
            }}
            aria-invalid={problemaDe("mensaje") !== undefined}
          />
          <Contador texto={formulario.mensaje} />
        </div>

        {/*
          ⭐ Los DOS campos que dependen del tipo. Ver el bloque ① del encabezado:
          no es una preferencia de formulario, es la unión discriminada del
          contrato dibujada — los campos que la otra variante no tiene, acá no
          existen.
        */}
        {formulario.tipo === "contencion" && (
          <>
            <div className="configuracion-chatbot__campo">
              <label htmlFor="guardrail-linea">Línea de ayuda</label>
              <textarea
                id="guardrail-linea"
                value={formulario.lineaDeAyuda}
                onChange={(e) => {
                  setFormulario({ ...formulario, lineaDeAyuda: e.target.value })
                }}
                aria-invalid={problemaDe("lineaDeAyuda") !== undefined}
              />
              {/*
                ⭐⭐ ⟦el-contador-de-contencion-dice-sumados⟧ · `R1.1-F3-CA-54`
                (`PRD_R1.1.md:743`) es **textual**: *«con Tipo Contención, Mensaje
                «Hola» y Línea de ayuda «Línea 600», se lee «13 / 4096
                (sumados)»»* — 4 + 9 = 13. Y el boceto
                `ConfigGuardrailCategoria.dc.html` lo dibuja igual:
                `<span class="mono">426 / 4096 (sumados)</span>`. **Las dos
                fuentes ponen «(sumados)» en el CONTADOR, entre paréntesis y
                detrás del número** — no sueltan la palabra en la prosa.

                ⛔ Hasta el 21/9/2026 acá decía «…sobre los dos sumados: 13 /
                4096»: tenía la palabra, pero **en otro lugar**, así que un
                `toContain("13 / 4096 (sumados)")` daba rojo. Medido ese día:
                `grep -rn "sumados"` sobre `src/`, `pruebas/`, `gates/` y
                `contracts/` devolvía **esta línea y un comentario de
                `contracts/agent/index.ts:1335`** — ni un examen, ni un gate.
                `R1.1-F3-CA-54` no estaba citado en ningún lado del repo.
                **Un criterio textual sin examen es un criterio que se rompe sin
                que nada avise.** Lo ejerce `pruebas/r11-pie-de-paneles.test.tsx`.

                ⚠️ Se sacó el «sumados» de la prosa («sobre los dos sumados» →
                «sobre los dos») para no decirlo dos veces en la misma oración.
                El boceto tampoco lo dice en su mitad de texto («Mensaje y línea
                salen en un solo envío»).

                ⚠️ El número va en UNA sola expresión y no partido en tres con
                `{" "}`: lo que el criterio fija es la CADENA, y JSX junta líneas
                con un espacio que es fácil de perder en la próxima edición.

                ⛔ Este NO es el `<Contador>` de `contador.tsx`: ése cuenta UN
                campo y lo usan Mensajes, Derivación y el «Mensaje» de acá
                arriba, donde no hay nada que sumar. Meterle «(sumados)» al
                componente compartido se lo pondría a las diez superficies.
              */}
              <span className="configuracion-chatbot__ayuda">
                Nombre y número del recurso oficial.
              </span>
              {/*
                ⭐ №16 · **EL PIE DEL CAMPO SON DOS MITADES**, como el boceto:
                `<div class="pie-campo"><span>Mensaje y línea salen en un solo
                envío</span><span class="mono">426 / 4096 (sumados)</span></div>`
                — texto a la izquierda, contador a la derecha. Es la misma
                anatomía que ya usan el pie de Personalidad y el del panel de
                mensaje (`pantalla.tsx`), que hasta hoy eran los dos únicos.

                ⛔⛔ **EL LITERAL «(sumados)» ES `R1.1-F3-CA-54` Y NO SE PUEDE
                PERDER AL PARTIR EL BLOQUE.** `PRD_R1.1.md:743` pide textual
                *«se lee «13 / 4096 (sumados)»»*. Por eso el número y el sufijo
                siguen en **UNA sola expresión interpolada, dentro de UN solo
                `<span>`**: partirlos en dos elementos o en tres nodos con
                `{" "}` rompería la contigüidad del `textContent` y el criterio
                dejaría de cumplirse **sin que el bloque se vea distinto**.
                Ya se reparó una vez (R-B, 21/9/2026); no se vuelve a perder.

                ⚠️ La prosa larga se acortó a «Nombre y número del recurso
                oficial.»: lo que explicaba la suma pasó a ser la mitad
                izquierda del pie, que es donde el boceto lo pone. **Ningún
                criterio fija ese texto** — `CA-48` habla de la ayuda de *Temas*,
                no de ésta.
              */}
              <div className="configuracion-chatbot__pie-campo">
                <span className="configuracion-chatbot__pie">
                  Mensaje y línea salen en un solo envío
                </span>
                <span className="configuracion-chatbot__contador">
                  {`${largo(formulario.mensaje) + largo(formulario.lineaDeAyuda)} / ${TOPE_AL_PARTICIPANTE} (sumados)`}
                </span>
              </div>
            </div>
            <p className="configuracion-chatbot__nota" role="note">
              Las categorías de contención siempre marcan el caso para revisión.
            </p>
          </>
        )}

        {formulario.tipo === "moderacion" && (
          <div className="configuracion-chatbot__campo">
            <label htmlFor="guardrail-marcar">
              <input
                id="guardrail-marcar"
                type="checkbox"
                checked={formulario.marcarParaRevision}
                onChange={(e) => {
                  setFormulario({ ...formulario, marcarParaRevision: e.target.checked })
                }}
              />
              Marcar para revisión
            </label>
          </div>
        )}

        {problemas.length === 0 ? null : (
          <ul className="configuracion-chatbot__problemas" role="alert">
            {problemas.map((problema) => (
              <li key={`${problema.campo}-${problema.motivo}`}>{problema.motivo}</li>
            ))}
          </ul>
        )}

        {/*
          ⭐⭐ ⟦el-pie-del-panel-lleva-Cancelar-PRIMERO⟧ · `sistema-visual.md:257`
          (§3.12 Panel lateral): *«El pie lleva «Cancelar» y el botón principal
          («Guardar …»)»*. `PRD_R1.1.md:669` lo repite en ese orden
          (*«…; «Cancelar» y «Guardar categoría»»*) y el boceto
          `ConfigGuardrailCategoria.dc.html:137` lo dibuja así
          (`<div class="dlg-pie"><span class="btn">Cancelar</span><span
          class="btn btn-p">Guardar categoría</span></div>`). Lo exige
          `R1.1-F5-CA-17`.

          ⚠️ El orden es de PIE DE PANEL, no de barra de pantalla: la barra global
          de `pantalla.tsx:816-880` pone «Guardar» primero y §3.12 no la alcanza.

          ⚠️ Esto NO toca `R1.1-F5-CA-74`, que habla de CUÁL es la acción
          principal, no de dónde está en el pie.
        */}
        <div className="configuracion-chatbot__acciones">
          {/*
            ⭐ La salida del panel, y **sale por donde entró**: editando es estado,
            así que se cierra vaciando el formulario; el alta vino por la
            DIRECCIÓN, así que se cierra volviendo a la dirección de la lista. Un
            «Cancelar» que sólo vaciara el estado dejaría la barra diciendo
            `…/guardrails/nueva` con el panel cerrado.
          */}
          {formulario.editando !== null ? (
            <button
              type="button"
              className="boton boton--chico"
              onClick={() => {
                setFormulario(FORMULARIO_VACIO)
              }}
              disabled={enCurso}
            >
              Cancelar
            </button>
          ) : (
            <a className="boton boton--chico" href={urlDeLaPestana("guardrails")}>
              Cancelar
            </a>
          )}
          <button
            type="button"
            className="boton boton--primario"
            onClick={guardar}
            disabled={enCurso || problemas.length > 0}
          >
            Guardar categoría
          </button>
        </div>
      </aside>
      )}
    </section>
  )
}
